import React, { memo, useRef } from 'react';
import { VariablePicker } from '../../DocEditor/components/VariablePicker';
import { useNestedClipboard } from './hooks/useNestedClipboard';
import { useNestedFieldFocus } from './hooks/useNestedFieldFocus';
import { useNestedMathInsert } from './hooks/useNestedMathInsert';
import { useNestedSlashField } from './hooks/useNestedSlashField';
import { useNestedStoredValue } from './hooks/useNestedStoredValue';
import { useNestedTyping } from './hooks/useNestedTyping';
import { useNestedValueSync } from './hooks/useNestedValueSync';
import { useNestedVarPicker } from './hooks/useNestedVarPicker';
import '../../DocEditor/styles/slash-menu.css';
import '../../DocEditor/styles/mentions.css';
import '../../DocEditor/styles/variable-pills.css';

export {
  nestedHtmlToValue,
  nestedValueToHtml,
  paintNestedValue,
  valueCaretOffsets,
} from './utils/nestedHtml';

/**
 * Value/description field that stores `{{name}}` tokens but shows the
 * resolved nested value in that variable's color. Typing "{{" opens the
 * shared VariablePicker.
 */
const NestedVarField = memo(({
  value,
  placeholder,
  className,
  ariaLabel,
  readOnly,
  onCommit,
  onFocus = null,
  autoFocus = false,
  resolveVariable,
  pickerVariables = [],
  onCreateVariable = null,
  historyEpoch = 0,
  slashAllowed = null,
}) => {
  const elRef = useRef(null);
  const store = useNestedStoredValue({ value, resolveVariable, elRef, readOnly, onCommit });
  const {
    picker,
    pickerOpenRef,
    openPickerAtCaret,
    insertNestedRef,
    closePicker,
  } = useNestedVarPicker({ elRef, readOnly, resolveVariable });
  const {
    mathMenu,
    mathMenuOpenRef,
    openMathAtCaret,
    openMathOnPill,
  } = useNestedMathInsert({
    elRef,
    readOnly,
    resolveVariable,
    pickerVariables,
    onCreateVariable,
    lastCommittedRef: store.lastCommittedRef,
    onCommit,
    readValue: store.storedValue,
  });
  const slash = useNestedSlashField({
    elRef,
    readOnly,
    slashAllowed,
    openMathAtCaret,
    openPickerAtCaret,
  });

  useNestedValueSync({
    elRef,
    value,
    paint: store.paint,
    resolveVariable,
    historyEpoch,
    autoFocus,
    readOnly,
    focusedRef: store.focusedRef,
    lastCommittedRef: store.lastCommittedRef,
    pickerOpenRef,
    slashOpenRef: slash.slashOpenRef,
  });

  const typing = useNestedTyping({
    elRef,
    readOnly,
    slashAllowed,
    slashOpenRef: slash.slashOpenRef,
    mathMenuOpenRef,
    pickerOpenRef,
    openAt: slash.openAt,
    setQuery: slash.setQuery,
    closeSlash: slash.closeSlash,
    caretMenuPosition: slash.caretMenuPosition,
    openMathAtCaret,
    openPickerAtCaret,
    allSelectedRef: store.allSelectedRef,
    heavyField: store.heavyField,
    ownsWholeValue: store.ownsWholeValue,
    selectEntireValue: store.selectEntireValue,
    clearAllSelected: store.clearAllSelected,
    clearStored: store.clearStored,
    writeStored: store.writeStored,
    readFull: store.readFull,
  });

  const clipboard = useNestedClipboard({
    elRef,
    readOnly,
    storedValue: store.storedValue,
    readFull: store.readFull,
    ownsWholeValue: store.ownsWholeValue,
    writeStored: store.writeStored,
    clearStored: store.clearStored,
    paint: store.paint,
    handleInput: typing.handleInput,
  });

  const focus = useNestedFieldFocus({
    elRef,
    readOnly,
    onFocus,
    focusedRef: store.focusedRef,
    allSelectedRef: store.allSelectedRef,
    lastCommittedRef: store.lastCommittedRef,
    paint: store.paint,
    commit: store.commit,
    clearAllSelected: store.clearAllSelected,
    pickerOpenRef,
    mathMenuOpenRef,
    slashOpenRef: slash.slashOpenRef,
    openMathOnPill,
  });

  return (
    <>
      <div
        ref={elRef}
        className={[
          'variables-panel__input',
          'variables-panel__textarea',
          'variables-panel__nested-field',
          className || '',
          readOnly ? 'is-readonly' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        aria-readonly={readOnly || undefined}
        data-placeholder={placeholder || ''}
        spellCheck={false}
        lang="zxx"
        autoCorrect="off"
        autoCapitalize="off"
        onFocus={focus.handleFocus}
        onPaste={clipboard.handlePaste}
        onCopy={clipboard.handleCopy}
        onCut={clipboard.handleCut}
        onPointerDown={focus.handlePointerDown}
        onKeyDown={typing.handleKeyDown}
        onBeforeInput={typing.handleBeforeInput}
        onBlur={focus.handleBlur}
        onInput={typing.handleInput}
      />
      {picker && (
        <VariablePicker
          position={{ x: picker.x, y: picker.y }}
          variables={pickerVariables}
          onPick={({ name, index }) => insertNestedRef(name, index)}
          onCreate={
            onCreateVariable
              ? (name, type) => {
                  onCreateVariable(name, type);
                  requestAnimationFrame(() => insertNestedRef(name));
                }
              : null
          }
          onClose={closePicker}
        />
      )}
      {slash.slashMenu}
      {mathMenu}
    </>
  );
});

export default NestedVarField;
